'use client';

import { createClient } from '@/lib/supabase/client';

function isSchemaError(error: any): boolean {
  if (!error) return false;
  
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    
    return schemaErrorPatterns.some(pattern => pattern.test(error.message));
  }
  
  return false;
}

export interface VolunteerApplicationData {
  fullName: string;
  email: string;
  phone?: string;
  opportunityId: number;
  opportunityTitle: string;
  location: string;
  skills: string[];
  motivation?: string;
  availability?: string;
}

export const volunteerApplicationService = {
  async apply(data: VolunteerApplicationData) {
    const supabase = createClient();

    try {
      const { data: result, error } = await supabase
        .from('volunteer_applications')
        .insert({
          full_name: data.fullName,
          email: data.email,
          phone: data.phone,
          opportunity_id: data.opportunityId,
          opportunity_title: data.opportunityTitle,
          location: data.location,
          skills: data.skills,
          motivation: data.motivation,
          availability: data.availability,
          status: 'pending'
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Volunteer application error:', error.message);
          return null;
        }
      }

      return {
        id: result.id,
        fullName: result.full_name,
        email: result.email,
        phone: result.phone,
        opportunityId: result.opportunity_id,
        opportunityTitle: result.opportunity_title,
        location: result.location,
        skills: result.skills,
        motivation: result.motivation,
        availability: result.availability,
        status: result.status,
        appliedAt: result.applied_at
      };
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async getAll() {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('volunteer_applications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else if (error.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return data?.map(row => ({
        id: row.id,
        fullName: row.full_name,
        email: row.email,
        phone: row.phone,
        opportunityId: row.opportunity_id,
        opportunityTitle: row.opportunity_title,
        location: row.location,
        skills: row.skills,
        motivation: row.motivation,
        availability: row.availability,
        status: row.status,
        appliedAt: row.applied_at
      })) || [];
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  }
};
function submitVolunteerApplication(...args: any[]): any {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: submitVolunteerApplication is not implemented yet.', args);
  return null;
}

export { submitVolunteerApplication };